import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Plus, Copy, Star, Pencil, Trash2 } from 'lucide-react';

// Выбор шаблона протокола. Оформление по «Бумаге» — заливка вместо рамок,
// бумажные тона, — но контрол остаётся СПИСКОМ, а не рядом чипов из артборда.
//
// 🔴 Почему не чипы. В артборде их пять и все короткие («Встреча», «Звонок»),
// потому что он рисовался на выдуманных данных. В проде у человека 17 шаблонов
// с именами до 41 знака («Протокол поисковой встречи(Брейншторминг)»), и рядом
// плашек из них не выйдет: получится сплошное полотно, в котором не найти
// нужный. Плюс у списка есть то, чего у чипов нет вовсе — шаблон по умолчанию,
// копирование системного к себе, правка и удаление своих.
// Поэтому из макета взято оформление, а форма контрола оставлена рабочей.
// Поиск добавлен по той же причине: семнадцать длинных имён глазами не
// просматривают.

export default function PromptSelector({
  prompts,
  selectedId,
  defaultPromptId,
  onSelect,
  onClone,
  onCreate,
  onSetDefault,
  onEdit,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [system, user] = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (p) => p.name.toLowerCase().includes(q);
    return [
      prompts.filter((p) => p.type === 'system' && match(p)),
      prompts.filter((p) => p.type !== 'system' && match(p)),
    ];
  }, [prompts, query]);
  const selected = prompts.find((p) => p._id === selectedId);

  const Row = ({ p, own }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={() => { onSelect(p); setOpen(false); }}
      onKeyDown={(e) => e.key === 'Enter' && (onSelect(p), setOpen(false))}
      className={`w-full flex items-center gap-2 rounded-[8px] px-[10px] py-2 text-[13px] text-left
                  cursor-pointer ${p._id === selectedId ? 'bg-paper-panel' : 'hover:bg-paper-panel'}`}
    >
      <span className={`w-2 h-2 rounded-full shrink-0
                        ${p._id === selectedId ? 'bg-paper-accent' : 'bg-paper-track'}`} />
      <span className="flex-1 truncate text-paper-ink">{p.name}</span>
      <Star
        className={`w-[14px] h-[14px] cursor-pointer shrink-0 ${
          p._id === defaultPromptId ? 'text-paper-warning fill-current' : 'text-paper-track hover:text-paper-warning'
        }`}
        onClick={(e) => { e.stopPropagation(); onSetDefault(p._id); }}
        title={p._id === defaultPromptId ? 'Шаблон по умолчанию' : 'Сделать по умолчанию'}
      />
      {own ? (
        <>
          <Pencil
            className="w-[14px] h-[14px] text-paper-track hover:text-paper-ink cursor-pointer shrink-0"
            onClick={(e) => { e.stopPropagation(); onEdit(p); setOpen(false); }}
            title="Редактировать"
          />
          <Trash2
            className="w-[14px] h-[14px] text-paper-track hover:text-paper-accent cursor-pointer shrink-0"
            onClick={(e) => { e.stopPropagation(); onDelete(p._id); setOpen(false); }}
            title="Удалить"
          />
        </>
      ) : (
        <Copy
          className="w-[14px] h-[14px] text-paper-track hover:text-paper-ink cursor-pointer shrink-0"
          onClick={(e) => { e.stopPropagation(); onClone(p._id); }}
          title="Скопировать к себе"
        />
      )}
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-[10px] bg-paper-bg px-3 py-2 text-[13px] text-paper-ink hover:bg-paper-panel transition-colors max-w-full"
      >
        <span className="truncate max-w-[260px]">{selected ? selected.name : 'Выберите шаблон'}</span>
        <span className="text-paper-ink-3">▾</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-[30rem] max-w-[80vw] bg-paper-card rounded-panel shadow-[0_12px_32px_rgba(0,0,0,.10)] z-50 p-2 flex flex-col gap-1">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Найти шаблон"
            className="w-full rounded-[8px] bg-paper-bg px-3 py-2 text-[13px] outline-none placeholder:text-paper-hint"
          />

          {/* «Создать шаблон» стоит СРАЗУ под поиском, а не под списком: панель
              высотой в пол-экрана уводила кнопку за нижний край, и добавить
              шаблон было нечем (жалоба пользователя 08.09.2026). */}
          <button
            type="button"
            onClick={() => { onCreate(); setOpen(false); }}
            className="flex items-center gap-2 rounded-[8px] px-[10px] py-2 text-[13px] text-paper-ink hover:bg-paper-panel"
          >
            <Plus className="w-4 h-4" /> Создать шаблон
          </button>

          <div className="max-h-[min(360px,50vh)] overflow-y-auto flex flex-col gap-[2px]">
            {/* Свои идут ПЕРВЫМИ. Раньше список открывался девятью системными, а
                свои лежали под ними за прокруткой: поиск их находил, а глазами
                человек их не видел и решал, что они пропали. */}
            {user.length > 0 && (
              <div className="flex items-baseline gap-2 px-[10px] pt-2 pb-1">
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">
                  Мои шаблоны
                </span>
                <span className="font-mono text-[11px] text-paper-hint">{user.length}</span>
              </div>
            )}
            {user.map((p) => <Row key={p._id} p={p} own />)}

            {system.length > 0 && (
              <div className="flex items-baseline gap-2 px-[10px] pt-3 pb-1">
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">
                  Системные варианты шаблонов
                </span>
                <span className="font-mono text-[11px] text-paper-hint">{system.length}</span>
              </div>
            )}
            {system.map((p) => <Row key={p._id} p={p} own={false} />)}

            {system.length === 0 && user.length === 0 && (
              <div className="px-[10px] py-3 text-[13px] text-paper-ink-3">Ничего не нашлось</div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
